import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Loader2, MessageSquareWarning } from "lucide-react";

interface ErrorReportFormProps {
  errorMessage?: string;
  errorStack?: string;
  errorName?: string;
  pathname?: string;
}

export function ErrorReportForm({
  errorMessage,
  errorStack,
  errorName,
  pathname,
}: ErrorReportFormProps) {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.fullName || !formData.email || !formData.message) {
      return;
    }

    setIsLoading(true);
    setSendError(false);

    try {
      const contactEndpoint = import.meta.env.VITE_CONTACT_ENDPOINT;
      const apiEndpoint = import.meta.env.VITE_BFF_URL + "/api/v1";

      if (!contactEndpoint || !apiEndpoint) {
        throw new Error("Contact endpoint not configured");
      }

      const errorDetails = [
        `[Error Report - Desktop App]`,
        `Página: ${pathname || "desconocida"}`,
        `Error: ${errorName || "Unknown"}: ${errorMessage || "Sin mensaje"}`,
        errorStack ? `Stack: ${errorStack.slice(0, 500)}` : "",
        `---`,
        `Mensaje del usuario: ${formData.message}`,
      ]
        .filter(Boolean)
        .join("\n");

      await fetch(`${apiEndpoint}${contactEndpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          companyName: "",
          message: errorDetails,
        }),
      });

      setSent(true);
    } catch {
      setSendError(true);
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        variant="outline"
        className="flex items-center gap-2 mx-auto"
      >
        <MessageSquareWarning className="w-4 h-4" />
        Reportar este error
      </Button>
    );
  }

  if (sent) {
    return (
      <div className="mt-4 p-4 rounded-lg border border-border bg-muted/50 max-w-md mx-auto text-center space-y-2">
        <CheckCircle2 className="w-8 h-8 text-green-500 mx-auto" />
        <p className="font-medium">Reporte enviado</p>
        <p className="text-sm text-muted-foreground">
          Gracias por reportar este error. Nuestro equipo ya fue notificado y
          está trabajando en solucionarlo. Si necesitás ayuda urgente,
          escribinos a soporte.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-4 p-4 rounded-lg border border-border bg-muted/50 max-w-md mx-auto space-y-3"
    >
      <p className="text-sm font-medium text-center">
        Ayudanos a resolver este problema
      </p>

      <Input
        placeholder="Tu nombre"
        value={formData.fullName}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, fullName: e.target.value }))
        }
        required
      />

      <Input
        type="email"
        placeholder="Tu email"
        value={formData.email}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, email: e.target.value }))
        }
        required
      />

      <Textarea
        placeholder="¿Qué estabas haciendo cuando ocurrió el error?"
        rows={3}
        value={formData.message}
        onChange={(e) =>
          setFormData((prev) => ({ ...prev, message: e.target.value }))
        }
        required
      />

      {sendError && (
        <p className="text-sm text-destructive text-center">
          No se pudo enviar el reporte. Por favor, intentá nuevamente.
        </p>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() => setOpen(false)}
          className="flex-1"
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading} className="flex-1">
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Enviando...
            </>
          ) : (
            "Enviar reporte"
          )}
        </Button>
      </div>
    </form>
  );
}
